import { isEqual } from 'lodash'
import { Context } from './context'
import { enumKeyToString } from './enumTools'
import { IdGenerator } from './idGenerator'
import { Logger } from './logger'
import { Id } from './model'
import { ObservableAction, ObservableValue, createObservableAction, createObservableValue } from './observable'

export interface BGOperationOptions {
  title: string
  totalSteps?: number
}

export enum BGOperationFinishStatus {
  SUCCEEDED,
  CANCELED,
}

export interface BGOperationInfo {
  id: string
  title: string
  subtitle?: string
  foreground?: boolean
  progress?: number | undefined
  totalSteps?: number | undefined
  finishStatus?: BGOperationFinishStatus | undefined
}

export class BGOperation {
  operationInfo: ObservableValue<BGOperationInfo>
  isCanceled: ObservableValue<boolean>

  constructor(readonly id: Id, opts: BGOperationOptions) {
    this.operationInfo = createObservableValue({
      id,
      title: opts.title,
      totalSteps: opts.totalSteps,
    } as BGOperationInfo)
    this.isCanceled = createObservableValue(false)
  }

  setTitle(ctx: Context, title: string) {
    this.operationInfo.setValue(
      {
        ...this.operationInfo.value,
        title,
      },
      ctx,
    )
  }

  setSubtitle(ctx: Context, subtitle: string) {
    this.operationInfo.setValue(
      {
        ...this.operationInfo.value,
        subtitle,
      },
      ctx,
    )
  }

  setProgress(ctx: Context, progress: number, totalSteps?: number) {
    this.operationInfo.setValue(
      {
        ...this.operationInfo.value,
        progress,
        totalSteps,
      },
      ctx,
    )
  }

  setForeground(ctx: Context, foreground: boolean) {
    this.operationInfo.setValue(
      {
        ...this.operationInfo.value,
        foreground,
      },
      ctx,
    )
  }

  finish(ctx: Context): void {
    this.operationInfo.setValue(
      {
        ...this.operationInfo.value,
        finishStatus: BGOperationFinishStatus.SUCCEEDED,
      },
      ctx,
    )
  }

  cancel(ctx: Context): void {
    this.operationInfo.setValue(
      {
        ...this.operationInfo.value,
        finishStatus: BGOperationFinishStatus.CANCELED,
      },
      ctx,
    )
    this.isCanceled.setValue(true, ctx)
  }
}

export interface BGOperationsService {
  readonly operationStarted: ObservableAction<BGOperationInfo>
  readonly operationFinished: ObservableAction<BGOperationInfo>
  readonly operationCanceled: ObservableAction<BGOperationInfo>
  readonly statusChanged: ObservableAction<BGOperationInfo>

  runWithOperation<R>(ctx: Context, opts: BGOperationOptions, block: (op: BGOperation) => Promise<R>): Promise<R>
}

export class DefaultBGOperationsService implements BGOperationsService {
  readonly operationStarted = createObservableAction<BGOperationInfo>()
  readonly operationFinished = createObservableAction<BGOperationInfo>()
  readonly operationCanceled = createObservableAction<BGOperationInfo>()
  readonly statusChanged = createObservableAction<BGOperationInfo>()

  private ops: Map<Id, BGOperation> = new Map()

  private logger = new Logger('BGOperationsService')

  get operations() {
    return this.ops.values()
  }

  constructor(private opts: { idGenerator: IdGenerator }) {}

  startOperation(ctx: Context, opts: BGOperationOptions): BGOperation {
    const id = this.opts.idGenerator.generate()
    this.logger.debug(ctx, '<23434ee0> Operation started', { ...opts, id })
    const op = new BGOperation(id, opts)
    this.ops.set(id, op)
    this.operationStarted.emit(op.operationInfo.value, ctx)

    let oldInfo: BGOperationInfo | undefined

    const infoSubscription = op.operationInfo.subscribe((info, ctx) => {
      if (info.finishStatus !== undefined) {
        this.logger.debug(ctx ?? {}, '<0b567885> Operation finished', {
          id,
          status: enumKeyToString(BGOperationFinishStatus, info.finishStatus),
        })
        op.operationInfo.unsubscribe(infoSubscription)
        op.isCanceled.unsubscribe(cancelSubscription)
        this.ops.delete(id)
        this.operationFinished.emit(info, ctx)
      } else {
        if (!isEqual(info, oldInfo)) {
          this.logger.debug(ctx ?? {}, '<44dda2db> Operation updated', { ...info })
          oldInfo = info
          this.statusChanged.emit(info, ctx)
        }
      }
    })

    const cancelSubscription = op.isCanceled.subscribe((isCanceled, ctx) => {
      if (isCanceled) {
        op.operationInfo.unsubscribe(infoSubscription)
        op.isCanceled.unsubscribe(cancelSubscription)
        this.ops.delete(id)
        this.operationCanceled.emit(op.operationInfo.value, ctx)

        this.logger.debug(ctx ?? {}, '<ff8e6e3b> Operation canceled', { ...op.operationInfo.value })
      }
    })
    return op
  }

  async runWithOperation<R>(
    ctx: Context,
    opts: BGOperationOptions,
    block: (op: BGOperation) => Promise<R>,
  ): Promise<R> {
    const op = this.startOperation(ctx, opts)
    try {
      return await block(op)
    } finally {
      op.finish(ctx)
    }
  }
}
